import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Chip,
  Divider,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import {
  listSkuScrapeHistory,
  deleteRetailPrice,
  RetailSkuScrapeHistory,
} from 'common/src/api/retail_skus';
import {
  RetailPrice,
  RetailPriceScrape,
  RetailTargetSku,
} from 'common/src/utils/types';
import EditPriceModal from './EditPriceModal';
import {
  EmptyState,
  LoadingState,
  useConfirm,
} from '../../../components/ui';

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sku: RetailTargetSku | null;
  isScraping?: boolean;
  /** Called after a price is edited or deleted, so the parent table can
   *  refresh its record counts / status. */
  onChanged?: () => void;
}

const formatDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString() : '—';

const formatNumber = (
  value: number | null | undefined,
  options: Intl.NumberFormatOptions = {}
) =>
  value == null
    ? '—'
    : value.toLocaleString(undefined, {
        maximumFractionDigits: 4,
        ...options,
      });

const formatLocalPrice = (price: RetailPrice) =>
  price.price_local == null
    ? '—'
    : `${formatNumber(price.price_local, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })} ${price.currency ?? ''}`.trim();

const UNIT_TO_KG: Record<string, number> = {
  kg: 1,
  kilogram: 1,
  kilograms: 1,
  g: 0.001,
  gram: 0.001,
  grams: 0.001,
  lb: 0.453592,
  lbs: 0.453592,
  oz: 0.0283495,
};

const COUNT_UNITS = new Set([
  'pack',
  'pk',
  'pcs',
  'pc',
  'stuks',
  'each',
  'pieza',
  'unidad',
  'unit',
]);

const PER_PIECE_KG_BY_SPECIES: Record<number, number> = {
  8: 0.08,
  2: 0.008,
};

const formatKg = (value: number | null | undefined) =>
  value == null
    ? '—'
    : `${formatNumber(value, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      })} kg`;

const getKgConversionDetail = (
  price: RetailPrice | undefined,
  speciesId: number
) => {
  if (!price) return '—';

  const value = price.package_size_value;
  const unit = price.package_size_unit?.toLowerCase().trim();
  const weightKg = price.weight_kg;

  if (value == null || !unit || weightKg == null) {
    return 'No se pudo normalizar el peso con los datos extraídos.';
  }

  if (unit in UNIT_TO_KG) {
    const factor = UNIT_TO_KG[unit];
    return `${formatNumber(value)} ${unit} x ${formatNumber(factor)} kg/${unit} = ${formatKg(weightKg)}`;
  }

  if (COUNT_UNITS.has(unit)) {
    const perPiece = PER_PIECE_KG_BY_SPECIES[speciesId];
    if (perPiece != null) {
      return `${formatNumber(value)} ${unit} x ${formatNumber(perPiece)} kg/unidad estimada = ${formatKg(weightKg)}`;
    }
  }

  return `Unidad "${unit}" sin regla de conversión a kg.`;
};

const getKgSourceLabel = (price: RetailPrice | undefined) => {
  if (!price?.weight_source) return undefined;
  return price.weight_source === 'explicit'
    ? 'Peso explícito en la página'
    : 'Peso estimado por unidad';
};

const scrapeStatus = (scrape: RetailPriceScrape) => {
  if (!scrape.parsed && !scrape.parse_error) {
    return (
      <Chip size='sm' color='warning' variant='flat'>
        Scrapeando
      </Chip>
    );
  }
  if (scrape.parse_error) {
    return (
      <Tooltip content={scrape.parse_error}>
        <Chip size='sm' color='danger' variant='flat'>
          Error
        </Chip>
      </Tooltip>
    );
  }
  return (
    <Chip size='sm' color='success' variant='flat'>
      Parseado
    </Chip>
  );
};

const SummaryMetric: React.FC<{
  label: string;
  value: React.ReactNode;
  help?: string;
}> = ({ label, value, help }) => (
  <div className='rounded-lg border border-default-200 bg-default-50 px-4 py-3'>
    <p className='text-xs text-default-500'>{label}</p>
    <div className='mt-1 text-lg font-semibold text-default-900'>{value}</div>
    {help && <p className='mt-1 text-xs text-default-500'>{help}</p>}
  </div>
);

type ChartCurrency = 'local' | 'usd';

const PriceEvolutionChart: React.FC<{
  prices: RetailPrice[];
  localCurrency: string;
}> = ({ prices, localCurrency }) => {
  const [mode, setMode] = useState<ChartCurrency>('local');

  // prices come ordered DESC; chart wants oldest first
  const sortedPrices = useMemo(
    () =>
      [...prices].sort(
        (a, b) =>
          new Date(a.captured_at).getTime() -
          new Date(b.captured_at).getTime()
      ),
    [prices]
  );

  // Build the series and remember which datapoint indices came from a
  // manual entry, so we can paint those markers a different colour.
  const { series, manualIndices } = useMemo(() => {
    const data: Array<{ x: number; y: number }> = [];
    const manualIdx: number[] = [];
    for (const p of sortedPrices) {
      const y = mode === 'local' ? p.price_local : p.price_usd_per_kg;
      if (y == null) continue;
      if (p.entry_method === 'manual') manualIdx.push(data.length);
      data.push({ x: new Date(p.captured_at).getTime(), y });
    }
    return {
      series: [
        {
          name: mode === 'local' ? `Precio ${localCurrency}` : 'USD / kg',
          data,
        },
      ],
      manualIndices: manualIdx,
    };
  }, [sortedPrices, mode, localCurrency]);

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: 'area',
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
      },
      colors: ['#BE123C'], // cherry-600
      stroke: { curve: 'smooth', width: 2 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.35,
          opacityTo: 0.05,
          stops: [0, 100],
        },
      },
      dataLabels: { enabled: false },
      markers: {
        size: 4,
        hover: { size: 6 },
        // Manual entries get a blue marker so they stand out from
        // the cherry-red automated scrapes.
        discrete: manualIndices.map((dataPointIndex) => ({
          seriesIndex: 0,
          dataPointIndex,
          fillColor: '#2563eb', // blue-600
          strokeColor: '#2563eb',
          size: 5,
        })),
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
          style: { fontSize: '11px' },
        },
      },
      yaxis: {
        labels: {
          formatter: (v: number) =>
            mode === 'usd'
              ? `$${v.toFixed(2)}`
              : `${v.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })} ${localCurrency}`,
          style: { fontSize: '11px' },
        },
      },
      tooltip: {
        x: { format: 'dd MMM yyyy HH:mm' },
        y: {
          formatter: (v: number) =>
            mode === 'usd'
              ? `$${v.toFixed(4)} USD/kg`
              : `${v.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })} ${localCurrency}`,
        },
      },
      grid: { strokeDashArray: 3, borderColor: '#e5e7eb' },
    }),
    [mode, localCurrency, manualIndices]
  );

  const validPoints = series[0]?.data.length ?? 0;

  return (
    <section className='space-y-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <div>
          <h4 className='text-base font-semibold'>Evolución del precio</h4>
          <div className='flex items-center gap-3 text-sm text-default-500'>
            <span>
              {validPoints} {validPoints === 1 ? 'punto' : 'puntos'} guardados
            </span>
            {manualIndices.length > 0 && (
              <span className='inline-flex items-center gap-1'>
                <span className='inline-block h-2.5 w-2.5 rounded-full bg-blue-600' />
                {manualIndices.length} manual
                {manualIndices.length > 1 ? 'es' : ''}
              </span>
            )}
          </div>
        </div>
        <ButtonGroup size='sm' variant='flat'>
          <Button
            color={mode === 'local' ? 'primary' : 'default'}
            onClick={() => setMode('local')}
          >
            {localCurrency}
          </Button>
          <Button
            color={mode === 'usd' ? 'primary' : 'default'}
            onClick={() => setMode('usd')}
          >
            USD / kg
          </Button>
        </ButtonGroup>
      </div>
      {validPoints < 2 ? (
        <EmptyState
          title={`Necesitamos al menos 2 lecturas con precio para graficar la evolución. Hoy hay ${validPoints}.`}
        />
      ) : (
        <div className='rounded-medium border border-default-200 p-4'>
          {/* key={mode} forces a fresh chart instance when the toggle
              flips. Without it react-apexcharts patches options in-place
              and the yaxis labels + tooltip formatter end up stuck on
              the previously-rendered mode. */}
          <ReactApexChart
            key={mode}
            options={options}
            series={series}
            type='area'
            height={260}
          />
        </div>
      )}
    </section>
  );
};

const SkuScrapeDetailModal: React.FC<Props> = ({
  isOpen,
  onClose,
  sku,
  isScraping = false,
  onChanged,
}) => {
  const [history, setHistory] = useState<RetailSkuScrapeHistory>({
    prices: [],
    scrapes: [],
  });
  const [loading, setLoading] = useState(false);
  const [editPrice, setEditPrice] = useState<RetailPrice | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const confirm = useConfirm();

  const loadHistory = useCallback(async () => {
    if (!sku) return;
    setLoading(true);
    try {
      const data = await listSkuScrapeHistory(sku.id);
      setHistory(data);
    } catch (e: unknown) {
      toast.error(`Error cargando historial: ${getErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  }, [sku]);

  useEffect(() => {
    if (!isOpen || !sku) return;
    loadHistory();
  }, [isOpen, sku, loadHistory]);

  const handleDeletePrice = async (price: RetailPrice) => {
    if (deletingId !== null) return;
    const ok = await confirm({
      title: `¿Eliminar el precio del ${new Date(
        price.captured_at
      ).toLocaleString()}?`,
      message: 'Se quita de la serie de tiempo. Esta acción no se puede deshacer.',
      isDestructive: true,
    });
    if (!ok) return;

    setDeletingId(price.id);
    try {
      await deleteRetailPrice(price.id);
      toast.success('Precio eliminado');
      await loadHistory();
      onChanged?.();
    } catch (e: unknown) {
      toast.error(`No se pudo eliminar: ${getErrorMessage(e)}`);
    } finally {
      setDeletingId(null);
    }
  };

  if (!sku) return null;

  const latestPrice = history.prices[0];
  const latestScrape = history.scrapes[0];
  const hasData = history.prices.length > 0 || history.scrapes.length > 0;

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size='5xl' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-2'>
          <div className='flex flex-wrap items-center gap-2'>
            <span>Detalle scraper retail</span>
            <Chip size='sm' variant='flat'>
              {sku.country}
            </Chip>
            {sku.tier && (
              <Chip
                size='sm'
                variant='flat'
                color={sku.tier === 'premium' ? 'warning' : 'default'}
              >
                {sku.tier}
              </Chip>
            )}
            {isScraping && (
              <Chip size='sm' color='warning' variant='flat'>
                Scrapeando ahora
              </Chip>
            )}
          </div>
          <p className='text-sm font-normal text-default-500'>
            {sku.retailer} · {sku.product_name}
          </p>
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <LoadingState label='Cargando historial...' />
          ) : (
            <div className='space-y-5'>
              {!hasData && (
                <EmptyState title='Este SKU todavía no tiene scrapes guardados.' />
              )}

              <section className='space-y-3'>
                <div className='flex items-center justify-between gap-3'>
                  <div>
                    <h4 className='text-base font-semibold'>
                      Última lectura validada
                    </h4>
                    <p className='text-sm text-default-500'>
                      Resumen para comparar rápido contra la PDP del retailer.
                    </p>
                  </div>
                  <a
                    href={sku.url}
                    target='_blank'
                    rel='noreferrer'
                    className='inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-primary-600 underline'
                  >
                    Abrir URL <Icon icon='tabler:external-link' className='text-xs' />
                  </a>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-4 gap-3'>
                  <SummaryMetric
                    label='Precio local'
                    value={latestPrice ? formatLocalPrice(latestPrice) : '—'}
                    help={latestPrice?.package_size_text ?? undefined}
                  />
                  <SummaryMetric
                    label='Precio convertido'
                    value={
                      latestPrice?.price_usd_per_kg == null
                        ? '—'
                        : `$${formatNumber(latestPrice.price_usd_per_kg, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} USD/kg`
                    }
                    help='Normalizado por peso'
                  />
                  <SummaryMetric
                    label='Peso usado'
                    value={formatKg(latestPrice?.weight_kg)}
                    help={getKgSourceLabel(latestPrice)}
                  />
                  <SummaryMetric
                    label='Dólar usado'
                    value={formatNumber(latestPrice?.fx_rate_used)}
                    help={
                      latestPrice?.currency
                        ? `USD -> ${latestPrice.currency}`
                        : undefined
                    }
                  />
                  <SummaryMetric
                    label='Último scrape'
                    value={formatDate(
                      latestPrice?.captured_at ?? latestScrape?.scraped_at
                    )}
                    help={
                      latestScrape && !latestScrape.parsed
                        ? 'Scrape pendiente'
                        : undefined
                    }
                  />
                </div>

                <div className='rounded-lg border border-default-200 bg-default-50 px-4 py-3'>
                  <p className='text-xs font-semibold uppercase text-default-500'>
                    Conversión a kg
                  </p>
                  <p className='mt-1 text-sm text-default-700'>
                    {getKgConversionDetail(latestPrice, sku.species_id)}
                  </p>
                </div>
              </section>

              <Divider />

              <PriceEvolutionChart
                prices={history.prices}
                localCurrency={sku.currency}
              />

              <Divider />

              <section className='space-y-3'>
                <div>
                  <h4 className='text-base font-semibold'>Precios extraídos</h4>
                  <p className='text-sm text-default-500'>
                    Filas limpias guardadas en la serie de tiempo.
                  </p>
                </div>
                <Table removeWrapper aria-label='Historial precios retail'>
                  <TableHeader>
                    <TableColumn>Fecha</TableColumn>
                    <TableColumn>Producto</TableColumn>
                    <TableColumn>Local</TableColumn>
                    <TableColumn>USD/kg</TableColumn>
                    <TableColumn>Kg usado</TableColumn>
                    <TableColumn>FX</TableColumn>
                    <TableColumn>Paquete</TableColumn>
                    <TableColumn>Promo</TableColumn>
                    <TableColumn>Acciones</TableColumn>
                  </TableHeader>
                  <TableBody emptyContent='Sin precios extraídos todavía'>
                    {history.prices.map((price) => (
                      <TableRow key={price.id}>
                        <TableCell className='text-xs text-default-500'>
                          <div className='flex items-center gap-2'>
                            <span>{formatDate(price.captured_at)}</span>
                            {price.entry_method === 'manual' && (
                              <Chip size='sm' variant='flat'>
                                Manual
                              </Chip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className='max-w-[220px] truncate'>
                          <Tooltip content={price.product_name}>
                            <span>{price.product_name}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{formatLocalPrice(price)}</TableCell>
                        <TableCell>
                          {price.price_usd_per_kg == null
                            ? '—'
                            : `$${formatNumber(price.price_usd_per_kg, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`}
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            content={getKgConversionDetail(
                              price,
                              sku.species_id
                            )}
                          >
                            <span>{formatKg(price.weight_kg)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {formatNumber(price.fx_rate_used)}
                        </TableCell>
                        <TableCell>
                          {price.package_size_text ?? (
                            <span className='text-default-400'>—</span>
                          )}
                        </TableCell>
                        <TableCell>{price.on_promo ? 'Sí' : 'No'}</TableCell>
                        <TableCell>
                          <div className='flex gap-1'>
                            <Tooltip content='Editar precio'>
                              <Button
                                isIconOnly
                                size='sm'
                                variant='light'
                                onClick={() => setEditPrice(price)}
                                isDisabled={deletingId !== null}
                              >
                                <Icon icon='tabler:edit' />
                              </Button>
                            </Tooltip>
                            <Tooltip content='Eliminar precio'>
                              <Button
                                isIconOnly
                                size='sm'
                                variant='light'
                                color='danger'
                                onClick={() => handleDeletePrice(price)}
                                isLoading={deletingId === price.id}
                                isDisabled={deletingId !== null}
                              >
                                <Icon icon='tabler:trash' />
                              </Button>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </section>

              <Divider />

              <section className='space-y-3'>
                <div>
                  <h4 className='text-base font-semibold'>
                    Intentos de scrape
                  </h4>
                  <p className='text-sm text-default-500'>
                    Capturas crudas de Firecrawl y estado del parser.
                  </p>
                </div>
                <Table removeWrapper aria-label='Intentos de scrape retail'>
                  <TableHeader>
                    <TableColumn>Fecha</TableColumn>
                    <TableColumn>Estado</TableColumn>
                    <TableColumn>HTTP</TableColumn>
                    <TableColumn>Notas</TableColumn>
                    <TableColumn>URL</TableColumn>
                  </TableHeader>
                  <TableBody emptyContent='Sin intentos guardados todavía'>
                    {history.scrapes.map((scrape) => (
                      <TableRow key={scrape.id}>
                        <TableCell className='text-xs text-default-500'>
                          {formatDate(scrape.scraped_at)}
                        </TableCell>
                        <TableCell>{scrapeStatus(scrape)}</TableCell>
                        <TableCell>{scrape.http_status ?? '—'}</TableCell>
                        <TableCell className='max-w-[280px] truncate'>
                          <Tooltip
                            content={
                              scrape.parse_error ??
                              scrape.parser_notes ??
                              'Sin notas'
                            }
                          >
                            <span>
                              {scrape.parse_error ??
                                scrape.parser_notes ??
                                'Sin notas'}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip content={scrape.source_url}>
                            <a
                              href={scrape.source_url}
                              target='_blank'
                              rel='noreferrer'
                              className='text-primary-500 underline text-xs'
                            >
                              Abrir
                            </a>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </section>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant='light' onClick={onClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    <EditPriceModal
      isOpen={editPrice !== null}
      onClose={() => setEditPrice(null)}
      price={editPrice}
      onSaved={() => {
        loadHistory();
        onChanged?.();
      }}
    />
    </>
  );
};

export default SkuScrapeDetailModal;
